/**
 * Modul-Kachel „Öffnungszeiten & Google-Eintrag": EINE reine Abbildung
 * `Datenlage -> Zustand -> Satz + erlaubte Handlungen`.
 *
 * Bauplan: docs/KONZEPT-RECHERCHE-SICHTBARKEIT.md § 4.6 (Mechanismus gegen B5), Kriterium R0.12
 * in docs/LOOP-RECHERCHE.md. Backend: `GET /api/settings/oeffnungszeiten/suche` +
 * `PUT /api/settings/oeffnungszeiten` (apps/api/src/routes/settings.ts), Tageslauf
 * `apps/api/src/lib/oeffnungszeiten-sweep.ts`, SD-Funktionen packages/db/sql/191.
 *
 * ── Warum ein eigenes Paket ───────────────────────────────────────────────────────────────────
 * `apps/web` hat KEINEN Test-Runner (Befund aus R0.2, in docs/LOOP-RECHERCHE.md festgehalten).
 * Eine Zustandslogik in der React-Komponente wäre unprüfbar — und genau hier hängt an ihr eine
 * Auflage von Antoine, die nicht leise verrutschen darf (siehe unten). Dieselbe Bauform wie
 * `@maestro/anreicherung` (R0.7) und `@maestro/firmenprofil` (R0.9); dieses Paket hat bewusst
 * KEIN `test`-Skript, seine Tests liegen in `apps/api/test/oeffnungszeiten-kachel.test.ts`, weil
 * genau diese Suite garantiert in der CI läuft (Ratsche `ci-testabdeckung.test.ts`).
 *
 * ── DIE AUFLAGE, die diese Datei durchsetzt ──────────────────────────────────────────────────
 * **Kein automatisches Bestätigen.** Antoines Auflage („damit wir nicht das falsche Lokal
 * abgleichen", `apps/api/src/lib/oeffnungszeiten-sweep.ts:5-7`): Ein Suchtreffer ist ein
 * VORSCHLAG, nie eine Verknüpfung. Solange `bestaetigtePlaceId` fehlt, gibt es hier keinen
 * Zustand, der so tut, als wäre der Betrieb verknüpft — der Treffer bekommt seinen eigenen
 * Zustand (`treffer_offen`) mit genau zwei Handlungen: bestätigen oder verwerfen.
 * Die Gegenprobe dazu liegt eine Ebene tiefer im Test: eine Suche allein verändert
 * `tenant_settings.google_place_id` nicht und lässt `oeffnungszeiten_faellig()` bei 0 Zeilen.
 *
 * ── Reinheit ─────────────────────────────────────────────────────────────────────────────────
 * Kein `new Date()` ohne Argument. `zeitzone` ist Parameter: ohne sie formatiert `Intl` in der
 * Zone der Laufzeit — im Browser also in der Zone des Betrachters, im Test deterministisch.
 */

/* ────────────────────────────── Eingabe ────────────────────────────────────────────────────── */

/**
 * Der Suchtreffer aus `GET /api/settings/oeffnungszeiten/suche`. **Nicht gespeichert** — er lebt
 * nur im Formular, bis ein Mensch ihn bestätigt oder verwirft.
 */
export interface EintragsTreffer {
  placeId: string;
  name: string | null;
  adresse: string | null;
  /** Je Wochentag eine Zeile, wie Google sie liefert. */
  zeiten: readonly string[];
}

/**
 * Was die Kachel über den eigenen Betrieb weiß. Alle Felder stammen aus
 * `GET /api/settings/module` (Spalten aus Migration 190).
 */
export interface EintragsLage {
  /**
   * Serverseitiger Google-Places-Zugang vorhanden (`/api/system/health` →
   * `integrations.places`). Ohne ihn antwortet die Suchroute 503 — ein Suchknopf wäre ein
   * leeres Versprechen.
   */
  zugangVorhanden: boolean;
  /** Firmenname UND Adresse gepflegt? Ohne beides antwortet die Suchroute 422 `keine_adresse`. */
  adresseVorhanden: boolean;
  /** Die EINMAL bestätigte Place-ID — `null` heißt: nicht verknüpft. */
  bestaetigtePlaceId: string | null;
  /** Der bestätigte Stand der Öffnungszeiten (`opening_hours`). */
  bestaetigteZeiten: readonly string[] | null;
  /** Was Google zuletzt gemeldet hat, WENN es abweicht (`opening_hours_pending`) — ein Vorschlag. */
  offenerVorschlag: readonly string[] | null;
  /** Wann der Tageslauf zuletzt abgeglichen hat (`opening_hours_synced_at`, ISO). */
  letzterAbgleich: string | null;
  /** Ergebnis der letzten Suche in DIESER Sitzung — `undefined` = es wurde noch nicht gesucht. */
  suchtreffer?: EintragsTreffer | null;
  /** IANA-Zone für „Stand …". Fehlt sie, gilt die Zone der Laufzeit (= des Betrachters). */
  zeitzone?: string;
}

/* ────────────────────────────── Ausgabe ────────────────────────────────────────────────────── */

/**
 * Die sieben unterscheidbaren Lagen. `treffer_offen` ist der Zustand, den die Auflage erzwingt:
 * gefunden, aber NICHT verknüpft.
 */
export const EINTRAGS_ZUSTAENDE = [
  "kein_zugang",
  "keine_adresse",
  "nicht_verknuepft",
  "kein_treffer",
  "treffer_offen",
  "verknuepft",
  "vorschlag_offen",
] as const;
export type EintragsZustand = (typeof EINTRAGS_ZUSTAENDE)[number];

/**
 * Welche Handlung die Kachel anbieten darf. Jede hat einen Weg im Backend — eine Schaltfläche
 * ohne Wirkung wäre derselbe Fehler wie ein Satz, der etwas Falsches behauptet (R0.7):
 *
 *  - `suchen`     → `GET /api/settings/oeffnungszeiten/suche`
 *  - `bestaetigen`→ `PUT /api/settings/oeffnungszeiten` `{ placeId }`
 *  - `verwerfen`  → rein örtlich: der ungespeicherte Treffer wird verworfen (kein Aufruf nötig,
 *                   weil die Suche nichts geschrieben hat — genau das ist die Auflage)
 *  - `uebernehmen`→ `PUT /api/settings/oeffnungszeiten` `{ vorschlagUebernehmen: true }`
 *  - `loesen`     → `PUT /api/settings/oeffnungszeiten` `{ placeId: null }`
 */
export type EintragsHandlungsArt = "suchen" | "bestaetigen" | "verwerfen" | "uebernehmen" | "loesen";

export interface EintragsHandlung {
  art: EintragsHandlungsArt;
  beschriftung: string;
  /** true = die Handlung ist die naheliegende (im Formular hervorgehoben). */
  betont?: boolean;
}

export interface EintragsAnzeige {
  zustand: EintragsZustand;
  /** Genau ein Satz, der die Lage benennt. */
  satz: string;
  /** Was hier und jetzt möglich ist — leer, wenn es nichts zu tun gibt. */
  handlungen: readonly EintragsHandlung[];
  /**
   * Herkunftszeile für die angezeigten Zeiten — harte Regel dieses Loops: keine Recherche-Angabe
   * ohne Quelle + Stand. `null`, wenn keine Zeiten angezeigt werden.
   */
  quelleUndStand: string | null;
  /** Ist der Betrieb verknüpft? Nur dann läuft der Tageslauf (`191:19-21`). */
  verknuepft: boolean;
}

/* ────────────────────────────── Formatierung ───────────────────────────────────────────────── */

/** „12.06.2026" in der Zone des Betrachters (bzw. der übergebenen Zone). */
function datum(iso: string, zeitzone?: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: zeitzone });
}

/* ────────────────────────────── R2.4: der Zähler ───────────────────────────────────────────── */

/**
 * Der Monatsverbrauch bezahlter Google-Abrufe, wie ihn `GET /api/settings/module` als
 * `placesBudget` liefert (Migration 229).
 */
export interface AbrufBudget {
  /** Bereits verbrauchte Abrufe im laufenden Kalendermonat. */
  verbraucht: number;
  /** Der Deckel — kommt aus `google_places_monatsdeckel()`, nie aus dem Frontend. */
  deckel: number;
  /** Erster Tag des Kalendermonats (Europe/Berlin) als `YYYY-MM-DD`. */
  monat: string;
}

export interface BudgetAnzeige {
  /** Ein Satz, der Stand, Grenze und — wenn nötig — die Folge benennt. */
  satz: string;
  /** Ist der harte Stopp erreicht? Dann läuft in diesem Monat kein bezahlter Abruf mehr. */
  erschoepft: boolean;
  /** 0…1, für einen Balken. Bei Deckel 0 ist der Anteil 1 (nichts ist möglich). */
  anteil: number;
}

const MONATSNAMEN = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
] as const;

/**
 * `2026-08-01` → „August 2026". Bewusst OHNE `new Date()`: das ISO-Datum kommt schon in
 * Europe/Berlin aus der Datenbank (`google_places_monat()`), und ein `new Date("2026-08-01")`
 * würde es als UTC-Mitternacht lesen und im Browser eines westlicheren Betrachters als Juli
 * anzeigen — genau die stille Verschiebung, gegen die die Zeitzonen-Festlegung in sql/229 steht.
 */
function monatsName(iso: string): string {
  const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(iso.trim());
  if (!m) return iso;
  const nr = Number(m[2]);
  const name = MONATSNAMEN[nr - 1];
  return name ? `${name} ${m[1]}` : iso;
}

/** `2026-08-01` → „1. September 2026" (der Tag, an dem das Budget wieder aufgeht). */
function naechsterMonatsErster(iso: string): string {
  const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(iso.trim());
  if (!m) return iso;
  const jahr = Number(m[1]);
  const nr = Number(m[2]);
  const nJahr = nr === 12 ? jahr + 1 : jahr;
  const nNr = nr === 12 ? 1 : nr + 1;
  const name = MONATSNAMEN[nNr - 1];
  return name ? `1. ${name} ${nJahr}` : iso;
}

/**
 * Der Zähler für die Kachel (Kriterium R2.4, Entscheidung P-5: 200 Abrufe/Monat je Mandant,
 * harter Stopp).
 *
 * Warum überhaupt eine reine Funktion und nicht drei Zeilen JSX: `apps/web` hat keinen
 * Test-Runner (R0.2). Der Satz behauptet etwas über Geld und über eine Grenze — genau die Sorte
 * Aussage, die nachweislich stimmen muss. Getestet wird er in `apps/api/test`.
 *
 * `null` bei fehlender Angabe: eine Kachel, die „0 von 0" behauptet, wäre eine Aussage über
 * einen Stand, den sie nicht kennt.
 */
export function budgetAnzeige(budget: AbrufBudget | null | undefined): BudgetAnzeige | null {
  if (!budget || !Number.isFinite(budget.verbraucht) || !Number.isFinite(budget.deckel)) return null;
  const verbraucht = Math.max(0, Math.trunc(budget.verbraucht));
  const deckel = Math.max(0, Math.trunc(budget.deckel));
  const erschoepft = verbraucht >= deckel;
  const anteil = deckel > 0 ? Math.min(1, verbraucht / deckel) : 1;
  const kopf = `Google-Abrufe im ${monatsName(budget.monat)}: ${verbraucht} von ${deckel}`;
  const satz = erschoepft
    ? `${kopf} — aufgebraucht. Bis zum ${naechsterMonatsErster(budget.monat)} wird nichts mehr bei Google abgerufen.`
    : `${kopf} — ${deckel - verbraucht} verbleiben.`;
  return { satz, erschoepft, anteil };
}

/** Name und Adresse zu einer Zeile — beides kann fehlen, dann bleibt die Place-ID die Identität. */
export function trefferZeile(t: EintragsTreffer): string {
  const teile = [t.name?.trim(), t.adresse?.trim()].filter((s): s is string => Boolean(s));
  return teile.length > 0 ? teile.join(", ") : t.placeId;
}

/* ────────────────────────────── Die Abbildung ──────────────────────────────────────────────── */

/**
 * Bildet die Datenlage auf genau einen Zustand, einen Satz und die erlaubten Handlungen ab.
 *
 * Reihenfolge der Prüfungen (jede Zeile hat einen Grund):
 *  1. **Verknüpft schlägt alles.** Ist der Eintrag bestätigt, ist die Suche gegenstandslos — und
 *     zwar dauerhaft: der Tageslauf fragt ab hier nur noch die ID ab, nie wieder eine Textsuche
 *     (`oeffnungszeiten-sweep.ts`). Ein Suchknopf neben einer bestehenden Verknüpfung lüde dazu
 *     ein, sie unbemerkt gegen einen Nachbar-Eintrag zu tauschen.
 *  2. Kein Server-Zugang → gar keine Handlung. 503 wäre die einzige Antwort.
 *  3. Fehlende Adresse vor der Suche: die Route antwortet 422 `keine_adresse`; der Knopf wäre ein
 *     Klick in eine Fehlermeldung.
 *  4. Ein Suchtreffer ist ein Vorschlag, keine Verknüpfung (die Auflage).
 *  5. Gesucht und nichts gefunden ist etwas anderes als „noch nie gesucht" — beides bekommt
 *     seinen eigenen Satz (Lehre aus R0.7: ein Einheitssatz für mehrere Lagen erklärt keine).
 */
export function eintragsAnzeige(lage: EintragsLage): EintragsAnzeige {
  const { zugangVorhanden, adresseVorhanden, bestaetigtePlaceId, offenerVorschlag, letzterAbgleich, zeitzone } = lage;

  // 1. Bereits verknüpft.
  if (bestaetigtePlaceId) {
    const abgleich = letzterAbgleich
      ? `Letzter Abgleich: ${datum(letzterAbgleich, zeitzone)}.`
      : "Noch kein Abgleich gelaufen — der Tageslauf holt die Zeiten in den nächsten 24 Stunden.";
    const loesen: EintragsHandlung = { art: "loesen", beschriftung: "Verknüpfung lösen" };
    const quelle = lage.bestaetigteZeiten && lage.bestaetigteZeiten.length > 0
      ? `Google-Eintrag${letzterAbgleich ? `, Stand ${datum(letzterAbgleich, zeitzone)}` : ", noch nicht abgeglichen"}`
      : null;
    if (offenerVorschlag && offenerVorschlag.length > 0) {
      return {
        zustand: "vorschlag_offen",
        satz: `Google meldet abweichende Öffnungszeiten. ${abgleich} Übernommen wird nichts von selbst.`,
        handlungen: [{ art: "uebernehmen", beschriftung: "Vorschlag übernehmen", betont: true }, loesen],
        quelleUndStand: quelle,
        verknuepft: true,
      };
    }
    return {
      zustand: "verknuepft",
      satz: `Eintrag bestätigt — der Abgleich läuft täglich. ${abgleich}`,
      handlungen: [loesen],
      quelleUndStand: quelle,
      verknuepft: true,
    };
  }

  // 2. Kein serverseitiger Zugang.
  if (!zugangVorhanden) {
    return {
      zustand: "kein_zugang",
      satz: "Für diesen Betrieb ist kein Google-Places-Zugang eingerichtet — die Suche bliebe wirkungslos.",
      handlungen: [],
      quelleUndStand: null,
      verknuepft: false,
    };
  }

  // 3. Ohne Name und Adresse gibt es nichts zu suchen.
  if (!adresseVorhanden) {
    return {
      zustand: "keine_adresse",
      satz: "Firmenname und Adresse fehlen — ohne beides findet die Suche in einer Großstadt das falsche Lokal.",
      handlungen: [],
      quelleUndStand: null,
      verknuepft: false,
    };
  }

  // 4. Gefunden, aber NICHT bestätigt — die Auflage.
  if (lage.suchtreffer) {
    return {
      zustand: "treffer_offen",
      satz: `Gefunden: ${trefferZeile(lage.suchtreffer)}. Noch nicht bestätigt — nichts wird abgeglichen, bevor Sie zustimmen.`,
      handlungen: [
        { art: "bestaetigen", beschriftung: "Das ist mein Betrieb", betont: true },
        { art: "verwerfen", beschriftung: "Nicht mein Betrieb" },
      ],
      quelleUndStand: lage.suchtreffer.zeiten.length > 0 ? "Google-Eintrag, ungeprüfter Suchtreffer" : null,
      verknuepft: false,
    };
  }

  // 5. Gesucht und nichts gefunden.
  if (lage.suchtreffer === null) {
    return {
      zustand: "kein_treffer",
      satz: "Kein Google-Eintrag zu Firmenname und Adresse gefunden. Beides in den Stammdaten prüfen und erneut suchen.",
      handlungen: [{ art: "suchen", beschriftung: "Erneut suchen" }],
      quelleUndStand: null,
      verknuepft: false,
    };
  }

  // Noch nie gesucht.
  return {
    zustand: "nicht_verknuepft",
    satz: "Noch kein Google-Eintrag bestätigt — der tägliche Abgleich ruht.",
    handlungen: [{ art: "suchen", beschriftung: "Meinen Google-Eintrag suchen", betont: true }],
    quelleUndStand: null,
    verknuepft: false,
  };
}
